import { useEffect, useState } from "react";
import axios from "axios";
import {
    ArrowUpRight,
    Loader2,
    MapPin,
    Briefcase,
    Send,
    CheckCircle2,
    Clock,
    Building2,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import DigtekNavbar from "@/components/digtek/DigtekNavbar";
import DigtekFooter from "@/components/digtek/DigtekFooter";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYMENT_LABELS = {
    "full-time": "Full-time",
    "part-time": "Part-time",
    contract: "Contract",
    freelance: "Freelance",
    internship: "Internship",
};

export default function CareersPage() {
    const [jobs, setJobs] = useState([]);
    const [busy, setBusy] = useState(true);
    const [openApply, setOpenApply] = useState(null); // job object or 'general'

    useEffect(() => {
        axios
            .get(`${API}/careers`)
            .then((r) => setJobs(r.data || []))
            .catch(() => toast.error("Could not load openings"))
            .finally(() => setBusy(false));
    }, []);

    return (
        <div className="digtek-page bg-ink text-bone min-h-screen grain">
            <DigtekNavbar />
            <main data-testid="careers-page">
                {/* Hero */}
                <section className="relative pt-36 pb-24 lg:pt-48 lg:pb-32 overflow-hidden">
                    <div
                        aria-hidden
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(circle at 70% 40%, rgba(200,122,56,0.08), transparent 50%)",
                        }}
                    />
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                            <div className="lg:col-span-8">
                                <div className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-ember mb-8">
                                    <span className="block w-8 h-px bg-ember" />
                                    Careers · Aximo Studios
                                </div>
                                <h1 className="font-display tracking-tightest text-bone text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
                                    Build the studio
                                    <br />
                                    <span className="font-serif-i italic text-ember">
                                        you'd want to work for.
                                    </span>
                                </h1>
                            </div>
                            <p className="lg:col-span-4 text-bone-300 text-base lg:text-lg leading-relaxed">
                                We hire creators, strategists and operators who
                                care obsessively about the craft — and ship
                                work that compounds. Remote-first, principal-led,
                                no agencies-in-suits energy.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Open positions */}
                <section className="py-16 lg:py-24">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
                            <h2 className="font-display text-bone text-4xl sm:text-5xl tracking-tightest leading-[1]">
                                Open positions.
                            </h2>
                            <button
                                onClick={() => setOpenApply("general")}
                                data-testid="general-apply-cta"
                                className="btn-outline-bone inline-flex items-center gap-2 h-12 px-5 text-sm tracking-wide"
                            >
                                Don't see your role? Submit a general application
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </div>

                        {busy ? (
                            <div className="text-bone-500 flex items-center gap-3">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Loading openings…
                            </div>
                        ) : jobs.length === 0 ? (
                            <div className="border border-dashed border-white/10 p-16 text-center">
                                <Briefcase className="w-8 h-8 text-bone-500 mx-auto mb-4" />
                                <p className="text-bone-300 text-lg mb-2">
                                    No open roles right now.
                                </p>
                                <p className="text-bone-500 text-sm mb-6">
                                    We hire continuously. Drop us a general
                                    application and we'll reach out when something
                                    matches.
                                </p>
                                <button
                                    onClick={() => setOpenApply("general")}
                                    data-testid="general-apply-empty"
                                    className="btn-ember inline-flex items-center gap-2 h-11 px-5 text-sm tracking-wide"
                                >
                                    Submit general application
                                    <ArrowUpRight className="w-4 h-4" />
                                </button>
                            </div>
                        ) : (
                            <ul
                                data-testid="jobs-list"
                                className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6"
                            >
                                {jobs.map((j, i) => (
                                    <li key={j.id}>
                                        <article
                                            data-testid={`job-${j.id}`}
                                            className="group border border-white/10 bg-ink-800/40 p-7 lg:p-8 hover:border-ember/40 hover:-translate-y-1 transition-all duration-300"
                                            style={{
                                                transitionDelay: `${i * 50}ms`,
                                            }}
                                        >
                                            <div className="flex items-start justify-between gap-3 mb-5">
                                                <span className="text-[10px] uppercase tracking-[0.25em] text-ember border border-ember/40 px-2 py-1">
                                                    {EMPLOYMENT_LABELS[
                                                        j.employment_type
                                                    ] || j.employment_type}
                                                </span>
                                                {j.salary_range && (
                                                    <span className="text-xs text-bone-500">
                                                        {j.salary_range}
                                                    </span>
                                                )}
                                            </div>
                                            <h3 className="font-display text-bone text-2xl tracking-tight mb-2">
                                                {j.title}
                                            </h3>
                                            {j.department && (
                                                <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3 inline-flex items-center gap-1">
                                                    <Building2 className="w-3 h-3" />
                                                    {j.department}
                                                </p>
                                            )}
                                            <p className="text-bone-300 text-sm leading-relaxed mb-5 line-clamp-3">
                                                {j.summary}
                                            </p>
                                            <div className="flex items-center justify-between gap-3 text-xs text-bone-500">
                                                <span className="inline-flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" />
                                                    {j.location}
                                                </span>
                                                <button
                                                    onClick={() => setOpenApply(j)}
                                                    data-testid={`apply-${j.id}`}
                                                    className="text-ember hover:text-bone uppercase tracking-[0.2em] inline-flex items-center gap-1 transition-colors"
                                                >
                                                    Apply
                                                    <ArrowUpRight className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </article>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                {/* Why join */}
                <section className="py-24 border-t border-white/10">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <h2 className="font-display text-bone text-4xl sm:text-5xl tracking-tightest leading-[1] mb-14">
                            Why{" "}
                            <span className="font-serif-i italic text-ember">
                                Aximo.
                            </span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                {
                                    n: "01",
                                    title: "Principal-led work",
                                    desc: "No layers. You own outcomes from day one and ship work you can sign your name to.",
                                },
                                {
                                    n: "02",
                                    title: "Remote-first, deliberate",
                                    desc: "Async by default, deep-work culture, two annual studio gatherings in person.",
                                },
                                {
                                    n: "03",
                                    title: "Compounding craft",
                                    desc: "Quarterly learning budget, paid study time, peer reviews — we get sharper every cycle.",
                                },
                            ].map((c) => (
                                <div
                                    key={c.n}
                                    className="border border-white/10 bg-ink-800/40 p-7"
                                >
                                    <p className="text-ember text-xs uppercase tracking-[0.25em] mb-4">
                                        {c.n}
                                    </p>
                                    <h3 className="font-display text-bone text-xl tracking-tight mb-3">
                                        {c.title}
                                    </h3>
                                    <p className="text-bone-300 text-sm leading-relaxed">
                                        {c.desc}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            <DigtekFooter />

            {openApply && (
                <ApplyModal
                    job={openApply === "general" ? null : openApply}
                    onClose={() => setOpenApply(null)}
                />
            )}
        </div>
    );
}

function ApplyModal({ job, onClose }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        location: "",
        role_interest: "",
        portfolio_url: "",
        linkedin_url: "",
        resume_url: "",
        cover_letter: "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Name and email are required");
            return;
        }
        setBusy(true);
        try {
            const url = job
                ? `${API}/careers/${job.id}/apply`
                : `${API}/careers/apply`;
            const payload = { ...form };
            if (job) delete payload.role_interest; // job-tagged apps don't need this
            await axios.post(url, payload);
            setDone(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="apply-modal"
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="bg-ink border border-white/15 max-w-2xl w-full p-7 lg:p-10 my-8 max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-6 gap-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-ember mb-2">
                            {job ? "Apply" : "General application"}
                        </p>
                        <h3 className="font-display text-bone text-3xl tracking-tightest">
                            {job ? job.title : "Tell us about yourself"}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="apply-close"
                        className="text-bone-500 hover:text-bone text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {done ? (
                    <div className="text-center py-10">
                        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
                        <h4 className="font-display text-bone text-2xl tracking-tightest mb-3">
                            Application received.
                        </h4>
                        <p className="text-bone-300 text-sm leading-relaxed mb-8 max-w-md mx-auto">
                            We review every application personally — if there's a
                            fit, you'll hear from us within 5–7 business days.
                        </p>
                        <button
                            onClick={onClose}
                            className="btn-ember inline-flex items-center gap-2 h-11 px-5 text-sm tracking-wide"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <form
                        onSubmit={onSubmit}
                        data-testid="apply-form"
                        className="space-y-5"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Full name" value={form.name} onChange={set("name")} testid="apply-name" />
                            <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="apply-email" />
                            <Field label="Phone (optional)" value={form.phone} onChange={set("phone")} testid="apply-phone" />
                            <Field label="Location" value={form.location} onChange={set("location")} testid="apply-location" placeholder="City, country" />
                        </div>
                        {!job && (
                            <Field
                                label="Role you're interested in"
                                value={form.role_interest}
                                onChange={set("role_interest")}
                                testid="apply-role"
                                placeholder="Editor, strategist, designer…"
                            />
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Portfolio URL" value={form.portfolio_url} onChange={set("portfolio_url")} testid="apply-portfolio" placeholder="https://" />
                            <Field label="LinkedIn URL" value={form.linkedin_url} onChange={set("linkedin_url")} testid="apply-linkedin" placeholder="https://linkedin.com/in/…" />
                        </div>
                        <Field label="Resume / CV link (Drive / Dropbox)" value={form.resume_url} onChange={set("resume_url")} testid="apply-resume" placeholder="https://drive.google.com/…" />
                        <label className="block">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                                A few lines about you (optional)
                            </span>
                            <textarea
                                value={form.cover_letter}
                                onChange={set("cover_letter")}
                                rows={4}
                                data-testid="apply-cover"
                                className="w-full bg-transparent border border-white/10 focus:border-ember outline-none p-3 text-bone placeholder:text-bone-500 text-sm transition-colors resize-none"
                                placeholder="What draws you to Aximo? Anything we should look at first?"
                            />
                        </label>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="apply-submit"
                            className="btn-ember w-full inline-flex items-center justify-center gap-2 h-12 px-5 text-sm tracking-wide disabled:opacity-60"
                        >
                            {busy ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Send application
                                </>
                            )}
                        </button>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-bone-500 text-center inline-flex items-center justify-center gap-1 w-full">
                            <Clock className="w-3 h-3" />
                            We respond to every application within 5–7 days
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, testid, type = "text", placeholder }) {
    return (
        <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                data-testid={testid}
                placeholder={placeholder}
                className="w-full bg-transparent border-0 border-b border-white/15 focus:border-ember outline-none py-2.5 text-bone placeholder:text-bone-500 text-sm transition-colors"
            />
        </label>
    );
}
